import { useReveal } from "../lib/useReveal";
import {
    ShieldCheck,
    Sparkles,
    Stethoscope,
    Heart,
    MapPinned,
    PhoneCall,
    Lock,
} from "lucide-react";

export default function Hero() {
    const ref = useReveal();

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            role="banner"
            className="relative overflow-hidden isolate"
        >
            <div className="absolute inset-0 z-0" aria-hidden="true">
                <div className="absolute inset-0 bg-[#070d22]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.12)_0%,_transparent_36%),radial-gradient(circle_at_bottom_left,_rgba(34,211,238,0.06)_0%,_transparent_30%)]" />
                <div className="absolute inset-0 opacity-[0.05] bg-[linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] bg-[size:72px_72px]" />
            </div>

            <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 md:pt-40 pb-24 md:pb-28">
                <div className="reveal text-center text-white max-w-4xl mx-auto">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[rgba(85,76,180,0.22)] backdrop-blur-md border border-white/10 text-sm font-medium text-white/90 mb-7 shadow-[0_10px_24px_rgba(13,18,44,0.25)]">
                        <Sparkles className="h-4 w-4 text-cyan-200" />
                        AI-Powered Private Recovery Support
                    </div>

                    <h1
                        id="hero-title"
                        className="text-[3.35rem] sm:text-[4.1rem] md:text-[5rem] lg:text-[5.5rem] font-bold tracking-[-0.04em] leading-[0.95] mb-7"
                    >
                        Your Recovery Journey
                        <span className="block text-white">
                            Starts <span className="text-emerald-300">Here</span>
                        </span>
                    </h1>

                    <p className="text-lg sm:text-[1.45rem] text-white/78 font-light leading-[1.45] max-w-3xl mx-auto mb-14">
                        A safer, more supportive way to stay on your recovery path. Talk to Hope,
                        check in honestly, journal privately, and find real-world recovery support
                        when you&apos;re ready.
                    </p>

                    <div
                        id="download"
                        className="flex flex-wrap justify-center items-center gap-3 md:gap-4"
                        role="list"
                        aria-label="Hero feature chips"
                    >
                        {[
                            {
                                Icon: ShieldCheck,
                                label: "Private AI companion",
                                tone: "text-cyan-200",
                            },
                            {
                                Icon: Heart,
                                label: "Daily check-ins",
                                tone: "text-emerald-200",
                            },
                            {
                                Icon: Lock,
                                label: "Encrypted journaling",
                                tone: "text-amber-200",
                            },
                            {
                                Icon: Stethoscope,
                                label: "Treatment directory",
                                tone: "text-yellow-200",
                            },
                            {
                                Icon: PhoneCall,
                                label: "Judgment-free support",
                                tone: "text-fuchsia-200",
                            },
                        ].map((item) => (
                            <div
                                key={item.label}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/6 backdrop-blur-sm border border-white/10 text-white/88 shadow-[0_10px_20px_rgba(6,9,25,0.22)]"
                                role="listitem"
                            >
                                <item.Icon className={`w-4 h-4 ${item.tone}`} />
                                <span className="text-sm md:text-[15px] font-medium">{item.label}</span>
                            </div>
                        ))}
                    </div>

                    <div className="mt-8 flex flex-wrap justify-center items-center gap-2 text-sm text-white/55">
                        <span>Looking for treatment?</span>
                        <a
                            href="/directory"
                            className="inline-flex items-center gap-2 text-white/78 hover:text-white transition-colors"
                            data-testid="hero-directory-link"
                        >
                            <MapPinned className="w-4 h-4 text-emerald-300" />
                            Browse the directory
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}
